/** @file Add-a-mapping row: slot, placeholder (a known one or the attachment's own name), attachment. */
import type { Doc, Id } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { useState } from 'react';
import { Button } from '../../ui-kit/Button';
import { Select, type SelectOption } from '../../ui-kit/Select';
import { slotPlaceholders } from './slotPlaceholders';

/** Props of AddSkinEntry; `attachments` are the Select options SkinEntries already built. */
export interface AddSkinEntryProps {
  doc: Doc;
  t: Translate;
  attachments: readonly SelectOption[];
  onAdd: (slot: Id, placeholder: string, attachment: Id) => void;
}

/** `chosen` while it is one of `options`, else the first option's value ('' when there are none). */
const valid = (options: readonly SelectOption[], chosen: string): string => (options.some((o) => o.value === chosen) ? chosen : (options[0]?.value ?? ''));

/** Slots in setup draw order; the empty placeholder choice means "the attachment's name". */
export function AddSkinEntry({ doc, t, attachments, onAdd }: AddSkinEntryProps) {
  const [chosenSlot, setSlot] = useState(doc.drawOrder[0] ?? '');
  const [chosenPlaceholder, setPlaceholder] = useState('');
  const [chosenAttachment, setAttachment] = useState(attachments[0]?.value ?? '');
  const slots = doc.drawOrder.map((id) => ({ value: id, label: doc.slots[id]?.name ?? id }));
  // A choice whose id left the document falls back to the first option (placeholder: to "same as attachment").
  const slot = valid(slots, chosenSlot);
  const attachment = valid(attachments, chosenAttachment);
  const placeholders = slotPlaceholders(doc, slot);
  const placeholder = placeholders.includes(chosenPlaceholder) ? chosenPlaceholder : '';
  const known = [{ value: '', label: t('panels.skins.sameAsAttachment') }, ...placeholders.map((p) => ({ value: p, label: p }))];
  const add = (): void => { onAdd(slot, placeholder === '' ? (doc.attachments[attachment]?.name ?? attachment) : placeholder, attachment); };
  return (
    <div className="rig-row">
      <Select label={t('panels.skins.slot')} value={slot} options={slots} onChange={(id) => { setSlot(id); setPlaceholder(''); }} />
      <Select label={t('panels.skins.placeholder')} value={placeholder} options={known} onChange={setPlaceholder} />
      <Select label={t('panels.skins.attachment')} value={attachment} options={attachments} onChange={setAttachment} />
      <Button label={t('panels.skins.addEntry')} disabled={slot === '' || attachment === ''} onClick={add} />
    </div>
  );
}
